import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api';
import { getDisplayError } from '../apiErrors';

export type ConversationSummary = {
  id: string;
  audience: 'INTERNAL' | 'CLIENT';
  entityType: string | null;
  entityId: string | null;
  subject: string | null;
  status: string;
  channel: string | null;
  clientContact: string | null;
  displayTitle: string;
  displaySubtitle: string | null;
  displayAvatarUrl?: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageFromViewer: boolean;
  unread: boolean;
};

type InboxFilters = {
  page?: number;
  limit?: number;
  audience?: 'INTERNAL' | 'CLIENT' | '';
  status?: 'OPEN' | 'CLOSED' | '';
  channel?: 'EMAIL' | 'WHATSAPP' | '';
  q?: string;
  unreadOnly?: boolean;
  excludeEntityTypes?: string[];
};

export function useConversationList(token: string | null, filters: InboxFilters) {
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const excludeEntityTypesKey = filters.excludeEntityTypes?.join(',') ?? '';

  const refresh = useCallback(async () => {
    if (!token) return;

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const task = (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (filters.page) params.set('page', String(filters.page));
        if (filters.limit) params.set('limit', String(filters.limit));
        if (filters.audience) params.set('audience', filters.audience);
        if (filters.status) params.set('status', filters.status);
        if (filters.channel) params.set('channel', filters.channel);
        if (filters.q?.trim()) params.set('q', filters.q.trim());
        if (filters.unreadOnly) params.set('unreadOnly', 'true');
        if (filters.excludeEntityTypes?.length) {
          params.set('excludeEntityTypes', filters.excludeEntityTypes.join(','));
        }
        const qs = params.toString();
        const res = await apiGet<{
          data: { items: ConversationSummary[]; total: number; unreadTotal: number };
        }>(`/conversations/inbox${qs ? `?${qs}` : ''}`, token);
        setItems(res.data.items ?? []);
        setTotal(res.data.total ?? 0);
        setUnreadTotal(res.data.unreadTotal ?? 0);
      } catch (e: unknown) {
        setError(getDisplayError(e));
      } finally {
        setLoading(false);
      }
    })();

    inFlightRef.current = task;
    try {
      await task;
    } finally {
      if (inFlightRef.current === task) {
        inFlightRef.current = null;
      }
    }
  }, [
    filters.audience,
    filters.channel,
    filters.limit,
    filters.page,
    filters.q,
    filters.status,
    filters.unreadOnly,
    excludeEntityTypesKey,
    token,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onReload = () => {
      void refresh();
    };
    window.addEventListener('wapharma-inbox-reload', onReload);
    return () => window.removeEventListener('wapharma-inbox-reload', onReload);
  }, [refresh]);

  return { items, total, unreadTotal, loading, error, refresh };
}
